import * as amplitude from "@amplitude/analytics-browser";
import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useContext, useSyncExternalStore } from "react";
import { PlatformEvents } from "../../platform-events";
import { resolveReturnsExperience } from "./returns-experience";

/** Kept from the details page's first rollout so an earlier switch back still holds. */
const LEGACY_STORAGE_KEY = "redo.return-details-legacy";
const LEGACY_PREFERENCE_CHANGED = "redo:return-details-legacy-changed";

/** Falls back to this when storage is unavailable, so a switch still applies to the tab. */
let inMemoryLegacyPreference = false;

function readLegacyPreference(): boolean {
  try {
    return localStorage.getItem(LEGACY_STORAGE_KEY) === "true";
  } catch {
    return inMemoryLegacyPreference;
  }
}

function writeLegacyPreference(prefersLegacy: boolean) {
  inMemoryLegacyPreference = prefersLegacy;
  try {
    if (prefersLegacy) {
      localStorage.setItem(LEGACY_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Private mode or quota exhausted; the in-memory value still applies.
  }
  window.dispatchEvent(new Event(LEGACY_PREFERENCE_CHANGED));
}

function subscribeToLegacyPreference(onChange: () => void) {
  window.addEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export interface ReturnsExperience {
  /** Whether the user may switch between the new experience and the old one. */
  canSwitch: boolean;
  useNewUi: boolean;
  switchToLegacy(): void;
  switchToNew(): void;
}

/**
 * The one place that reads the returns experience flag and the user's switch.
 * The flag arrives with the team, so there is no pending state.
 */
export function useReturnsExperience(): ReturnsExperience {
  const team = useContext(TeamContext);
  const prefersLegacy = useSyncExternalStore(
    subscribeToLegacyPreference,
    readLegacyPreference,
  );

  const switchToLegacy = useHandler(() => {
    writeLegacyPreference(true);
    amplitude.track(PlatformEvents.RETURN_DETAILS_EXPERIENCE_SWITCHED, {
      to: "legacy",
    });
  });

  const switchToNew = useHandler(() => {
    writeLegacyPreference(false);
    amplitude.track(PlatformEvents.RETURN_DETAILS_EXPERIENCE_SWITCHED, {
      to: "new",
    });
  });

  const { useNewUi, canSwitch } = resolveReturnsExperience({
    teamInRollout:
      team?.featureFlags?.[ReturnsFlagKey.RETURN_DETAILS_V2] === true,
    prefersLegacy,
  });

  return { canSwitch, useNewUi, switchToLegacy, switchToNew };
}
